import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const MESI_FULL = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const GIORNI_SETT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const TIPO_CONFIG = {
  lavoro: { bg: "bg-emerald-100 text-emerald-800", dot: "bg-emerald-500" },
  riposo: { bg: "bg-gray-100 text-gray-500", dot: "bg-gray-400" },
  assente: { bg: "bg-red-100 text-red-700", dot: "bg-red-500" },
  permesso: { bg: "bg-amber-100 text-amber-800", dot: "bg-amber-500" },
  malattia: { bg: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  ferie: { bg: "bg-violet-100 text-violet-700", dot: "bg-violet-500" },
  lavoro_permesso: { bg: "bg-yellow-50 text-yellow-800", dot: "bg-yellow-500" },
  vuoto: { bg: "", dot: "" }
};

const PresenzeMensile = ({ localeCorrente, getAuthHeaders }) => {
  const now = new Date();
  const [anno, setAnno] = useState(now.getFullYear());
  const [mese, setMese] = useState(now.getMonth() + 1);
  const [griglia, setGriglia] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const tableRef = useRef(null);

  const fetchGriglia = useCallback(async () => {
    if (!localeCorrente?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/personale/presenze/griglia-mensile`, {
        params: { locale_id: localeCorrente.id, anno, mese },
        headers: getAuthHeaders()
      });
      setGriglia(res.data);
    } catch {
      toast.error("Errore nel caricamento griglia");
    } finally {
      setLoading(false);
    }
  }, [localeCorrente?.id, anno, mese, getAuthHeaders]);

  useEffect(() => { fetchGriglia(); }, [fetchGriglia]);

  const giorniDelMese = useMemo(() => {
    if (!griglia) return [];
    return Array.from({ length: griglia.giorni_nel_mese }, (_, i) => {
      const d = new Date(anno, mese - 1, i + 1);
      return { num: i + 1, giorno: GIORNI_SETT[d.getDay() === 0 ? 6 : d.getDay() - 1], isDom: d.getDay() === 0 };
    });
  }, [griglia, anno, mese]);

  const handleExport = async () => {
    if (!localeCorrente?.id) return;
    setExporting(true);
    try {
      const res = await axios.get(`${API}/personale/presenze/export-mensile`, {
        params: { locale_id: localeCorrente.id, anno, mese },
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Presenze_${MESI_FULL[mese - 1]}_${anno}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Excel esportato con successo");
    } catch {
      toast.error("Errore nell'esportazione");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="presenze-mensile">
      {/* Selettore Mese/Anno */}
      <div className="flex items-center justify-between bg-card rounded-2xl p-3 border border-border">
        <Button variant="ghost" size="icon" onClick={() => {
          if (mese === 1) { setMese(12); setAnno(a => a - 1); }
          else setMese(m => m - 1);
        }} data-testid="mese-prev">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="text-center">
          <p className="text-base font-bold text-foreground">{MESI_FULL[mese - 1]} {anno}</p>
        </div>
        <Button variant="ghost" size="icon" onClick={() => {
          if (mese === 12) { setMese(1); setAnno(a => a + 1); }
          else setMese(m => m + 1);
        }} disabled={anno >= now.getFullYear() && mese >= now.getMonth() + 1} data-testid="mese-next">
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Month pills for quick navigation */}
      <div className="overflow-x-auto hide-scrollbar">
        <div className="flex gap-1.5 min-w-max">
          {MESI.map((m, idx) => (
            <button
              key={idx}
              onClick={() => setMese(idx + 1)}
              className={cn(
                "px-3 py-1.5 rounded-full text-xs font-semibold transition-all active:scale-95",
                mese === idx + 1
                  ? "bg-primary text-white shadow-sm"
                  : "bg-muted/60 text-muted-foreground hover:bg-muted"
              )}
              data-testid={`mese-pill-${idx + 1}`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : !griglia || griglia.dipendenti.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <p className="text-sm">Nessun dipendente trovato per questo locale</p>
        </div>
      ) : (
        <>
          {/* Griglia tabella */}
          <div className="border border-border rounded-xl overflow-hidden" ref={tableRef}>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-max">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 bg-card border-b border-r border-border px-3 py-2 text-left text-xs font-bold text-foreground min-w-[140px]">
                      Dipendente
                    </th>
                    {giorniDelMese.map(g => (
                      <th
                        key={g.num}
                        className={cn(
                          "border-b border-r border-border px-0.5 py-1 text-center min-w-[38px]",
                          g.isDom ? "bg-red-50" : "bg-muted/30"
                        )}
                      >
                        <div className="text-[10px] font-bold text-foreground">{g.num}</div>
                        <div className={cn("text-[9px]", g.isDom ? "text-red-500 font-semibold" : "text-muted-foreground")}>{g.giorno}</div>
                      </th>
                    ))}
                    <th className="sticky right-0 z-10 bg-card border-b border-l border-border px-2 py-2 text-center text-xs font-bold text-foreground min-w-[50px]">
                      Tot.
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {griglia.dipendenti.map((dip) => (
                    <tr key={dip.id} className="border-b border-border last:border-b-0 hover:bg-muted/10" data-testid={`griglia-row-${dip.id}`}>
                      <td className="sticky left-0 z-10 bg-card border-r border-border px-3 py-2">
                        <p className="text-xs font-semibold text-foreground truncate max-w-[130px]">{dip.nome}</p>
                        <p className="text-[10px] text-muted-foreground">{dip.ore_settimanali}h/sett</p>
                      </td>
                      {giorniDelMese.map(g => {
                        const cell = dip.giorni[String(g.num)] || { tipo: "vuoto", label: "" };
                        const config = TIPO_CONFIG[cell.tipo] || TIPO_CONFIG.vuoto;
                        return (
                          <td
                            key={g.num}
                            className={cn(
                              "border-r border-border px-0 py-1 text-center",
                              g.isDom && !cell.label && "bg-red-50/50"
                            )}
                          >
                            {cell.label && (
                              <span className={cn(
                                "inline-block px-1 py-0.5 rounded text-[10px] font-bold leading-none",
                                config.bg
                              )}>
                                {cell.label}
                              </span>
                            )}
                          </td>
                        );
                      })}
                      <td className="sticky right-0 z-10 bg-card border-l border-border px-2 py-2 text-center">
                        <span className="text-xs font-bold text-foreground">{dip.totali.ore_lavorate}h</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Legenda */}
          <div className="flex flex-wrap gap-3 px-1">
            {[
              { label: "Nh", desc: "Ore lavorate", tipo: "lavoro" },
              { label: "R", desc: "Riposo", tipo: "riposo" },
              { label: "A", desc: "Assente", tipo: "assente" },
              { label: "P", desc: "Permesso", tipo: "permesso" },
              { label: "M", desc: "Malattia", tipo: "malattia" },
              { label: "F", desc: "Ferie", tipo: "ferie" },
            ].map(item => (
              <div key={item.tipo} className="flex items-center gap-1.5">
                <span className={cn("w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center", TIPO_CONFIG[item.tipo].bg)}>
                  {item.label}
                </span>
                <span className="text-[11px] text-muted-foreground">{item.desc}</span>
              </div>
            ))}
          </div>

          {/* Bottone Export */}
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="w-full gap-2 py-3 rounded-xl font-semibold active:scale-[0.98]"
            data-testid="export-excel-btn"
          >
            <Download className="h-4 w-4" />
            {exporting ? "Esportazione in corso..." : "Esporta Excel per Consulente"}
          </Button>
        </>
      )}
    </div>
  );
};

export default PresenzeMensile;
